import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '../../../../../components/forms/Input';
import { Select } from '../../../../../components/forms/Select';
import { Button } from '../../../../../components/Button';
import { normalizeCpfCnpj } from '../../../../../utils/mask';
import { ANNUAL_BILLING_OPTIONS, COMPANY_TYPE_OPTIONS } from '../../constants';
import {
  companyStepSchema,
  CompanyStepValues,
  shareholdingsTotal,
} from '../../schemas';
import { ICashMeAddress } from '../../types';
import { AddressFields } from '../AddressFields/AddressFields';
import { WizardShell } from '../WizardShell/WizardShell';
import styles from '../WizardShell/WizardShell.module.css';

interface ICompanyStep {
  collateralAddress: ICashMeAddress;
  useCollateralAddressDefault: boolean;
  defaultValues: Partial<CompanyStepValues>;
  onBack: () => void;
  onSubmit: (values: CompanyStepValues) => void;
}

const EMPTY_SHAREHOLDER = {
  document: '',
  percentage: undefined as unknown as number,
};

export function CompanyStep({
  collateralAddress,
  useCollateralAddressDefault,
  defaultValues,
  onBack,
  onSubmit,
}: ICompanyStep) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setFocus,
    control,
    formState: { errors },
  } = useForm<CompanyStepValues>({
    resolver: zodResolver(companyStepSchema),
    mode: 'onBlur',
    defaultValues: {
      useCollateralAddress: useCollateralAddressDefault,
      companyType: '',
      responsibleName: '',
      annualBilling: '',
      shareholdings: [EMPTY_SHAREHOLDER],
      ...defaultValues,
      address: {
        zipCode: '',
        number: '',
        street: '',
        district: '',
        city: '',
        state: '',
        ...defaultValues.address,
      },
    },
  });

  const useCollateralAddress = watch('useCollateralAddress');
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'shareholdings',
  });
  const total = shareholdingsTotal(watch('shareholdings'));
  const shareholdingsError =
    errors.shareholdings?.message ?? errors.shareholdings?.root?.message;

  const submit = handleSubmit((values) =>
    onSubmit(
      values.useCollateralAddress
        ? { ...values, address: collateralAddress as any }
        : values
    )
  );

  return (
    <WizardShell
      step={2}
      totalSteps={4}
      title="Sobre a empresa"
      nextTitle="Renda e finalidade"
      helper="Agora precisamos de alguns dados da empresa para seguir com a análise."
    >
      <form onSubmit={submit} noValidate>
        <h2 className={styles.stepHeading}>Endereço da empresa</h2>

        <label className={styles.checkbox}>
          <input type="checkbox" {...register('useCollateralAddress')} />A
          empresa fica no endereço do imóvel em garantia.
        </label>

        {!useCollateralAddress && (
          <AddressFields
            prefix="address"
            register={register}
            errors={errors.address}
            setValue={setValue}
            setFocus={setFocus}
          />
        )}

        <h2 className={styles.stepHeading}>Dados da empresa</h2>

        <div className={styles.field}>
          <Select
            label="Tipo de empresa"
            placeholder="Selecione"
            options={COMPANY_TYPE_OPTIONS}
            errors={errors.companyType?.message}
            {...register('companyType')}
          />
        </div>

        <div className={styles.field}>
          <Input
            type="text"
            label="Nome do responsável (opcional)"
            placeholder="Nome do responsável"
            autoComplete="off"
            errors={errors.responsibleName?.message}
            {...register('responsibleName')}
          />
        </div>

        <div className={styles.field}>
          <Select
            label="Faturamento anual (opcional)"
            placeholder="Selecione"
            options={ANNUAL_BILLING_OPTIONS}
            errors={errors.annualBilling?.message}
            {...register('annualBilling')}
          />
        </div>

        <h2 className={styles.stepHeading}>Sócios</h2>

        {fields.map((item, index) => (
          <div className={styles.row} key={item.id}>
            <div className={styles.field}>
              <Input
                type="text"
                label="CPF ou CNPJ do sócio"
                placeholder="000.000.000-00"
                autoComplete="off"
                errors={errors.shareholdings?.[index]?.document?.message}
                {...register(`shareholdings.${index}.document`, {
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setValue(
                      `shareholdings.${index}.document`,
                      normalizeCpfCnpj(event.target.value)
                    ),
                })}
              />
            </div>
            <div className={styles.field}>
              <Input
                type="number"
                label="Participação (%)"
                placeholder="0"
                min={0}
                max={100}
                step={1}
                inputMode="numeric"
                errors={errors.shareholdings?.[index]?.percentage?.message}
                {...register(`shareholdings.${index}.percentage`, {
                  valueAsNumber: true,
                })}
              />
              {fields.length > 1 && (
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => remove(index)}
                >
                  Remover sócio
                </button>
              )}
            </div>
          </div>
        ))}

        <button
          type="button"
          className={styles.linkButton}
          onClick={() => append(EMPTY_SHAREHOLDER)}
        >
          + Adicionar sócio
        </button>

        {shareholdingsError ? (
          <p className={styles.errorText}>{shareholdingsError}</p>
        ) : (
          total < 100 && (
            <p className={styles.warningText}>
              As participações somam {total}%. O ideal é que somem 100% — a
              proposta pode seguir, mas a CashMe vai sinalizar a diferença.
            </p>
          )
        )}

        <div className={styles.footer}>
          <Button type="button" color="flat" text="Voltar" onClick={onBack} />
          <Button type="submit" color="primary" text="Continuar" />
        </div>
      </form>
    </WizardShell>
  );
}
